<template>
  <b-container>
    <LoadingIndicator 
      v-if="loading"
      topic="Organization"
    />
    <b-row>
      <b-col>
        <BaseMessageBlock :messages="errors"></BaseMessageBlock>
      </b-col>
    </b-row>
    <div v-if="!loading && organization !== null">
      <b-row class="text-left">
        <b-col></b-col>
        <b-col cols="auto" sm="6"  class="mb-3 mt-3">
          <h2>{{organization.name}}</h2> 
          <b-button @click="editOrganization" >Edit</b-button>
        </b-col>
        <b-col cols="auto" sm="4" class="mt-3">
          <b-button 
            class="float-right"
            to="/organization/list">
            Back to list
          </b-button>
        </b-col>
        <b-col></b-col>
      </b-row>
      <b-row class="text-left">
        <b-col></b-col>
        <b-col cols="5">
        </b-col>
        <b-col cols="5" class="text-right">
          <b-button variant="success" @click="showUserModal">Add User</b-button>
        </b-col>
        <b-col></b-col>
      </b-row>
      <b-row class="text-left">
        <b-col></b-col>
        <b-col cols="10">
          <h3>Users</h3>
          <b-table striped hover :items="users" :fields="userListFields">
            <template v-slot:cell(edit_relation)="data">
              <b-button-group class="float-left">
                <b-button
                  size="sm"
                  @click="deleteRelation(data.item.userId)"
                  variant="danger"
                  title="Remove user"
                  class="float-left">
                  <font-awesome-icon :icon="['fas', 'times']" size ="lg"></font-awesome-icon>
                </b-button>
              </b-button-group>
            </template>
            <template v-slot:cell(userName)="data">
              <b-link :to="'/user/' + data.item.userId">{{data.item.userName}}</b-link>
            </template>
            <template v-slot:cell(permissionLevel)="data">
              {{getLevelName(data.item.permissionLevel)}}
            </template>
          </b-table>
        </b-col>
        <b-col></b-col>
      </b-row>
      <b-row class="text-left">
        <b-col></b-col>
        <b-col cols="10" class="mt-4">
          <h3>Projects</h3>
          <b-table striped hover :items="projects" :fields="projectListFields">
            <template v-slot:cell(projectName)="data">
              <b-link :to="'/project/' + data.item.projectId">{{data.item.projectName}}</b-link>
            </template>
            <template v-slot:cell(role)="data">
              {{data.item.role}}
            </template>
            <template v-slot:cell(isPrimary)="data">
              {{data.item.isPrimary ? '✓' : ''}}
            </template>
          </b-table>
        </b-col>
        <b-col></b-col>
      </b-row>
    </div>
    <EditOrganizationModal
      v-if="organization"
      ref="edit-organization-modal"
      :organization="organization"
      @submit="onEditOrganizationSubmit"
    ></EditOrganizationModal>
    <AddOrgUserModal
      v-if="organization"
      ref="add-org-user-modal"
      :organizationId="organization.organizationId"
      @submit="onAddOrgUserSubmit">
    </AddOrgUserModal>
    <BaseConfirmationModal
      v-if="organization"
      ref="confirm-remove-modal"
      @submit="onDeleteRelationSubmit"
      @cancel="onDeleteRelationCancel"
      :title="'Remove user from ' + organization.name"
      okText="Yes"
      :message="'Are you sure you want to remove this user from ' + organization.name + '?'">
    </BaseConfirmationModal>
  </b-container>
</template>

<script>
import axios from "axios";
import AddOrgUserModal from "@/modal-components/AddOrgUserModal.vue";
import BaseConfirmationModal from "@/components/BaseConfirmationModal.vue";
import EditOrganizationModal from "@/modal-components/EditOrganizationModal.vue";

export default {
  name: "Organization",
  components: {
    AddOrgUserModal,
    BaseConfirmationModal,
    EditOrganizationModal
  },
  data() {
    return {
      errors: [],
      loading: false,
      organization: null,
      users: [],
      projects: [],
      userToRemove: null,
      userListFields: [
        { key: "edit_relation", label: ""},
        { key: "userName", label: "Name", sortable: true },
        { key: "permissionLevel", label: "Level", sortable: true }
      ],
      projectListFields: [
        { key: "projectName", label: "Name", sortable: true },
        { key: "role", label: "Role", sortable: true,
          tdClass: (value, key, item) => { return this.getRoleClass(item.role); } },
        { key: "isPrimary", label: "Primary?", sortable: true,
          tdClass: 'flag' }
      ]
    };
  },
  mounted() {
    if (!this.isSystemAdmin) {
      this.defaultRedirect();
    }
    let id = this.$route.params.id;
    if (!id) {
      this.$router.push({ name: 'Home' });
    }
    this.loadOrganization(id);
  },
  methods: {
    loadOrganization(id) {
      this.loading = true;
      axios
        .get(`${this.$api_url}/organizations/${id}/summary`, this.getReqConfig())
        .then(response => {
          // this.summary = response.data;
          this.organization = response.data.organization;
          this.projects = response.data.projects;
          this.users = response.data.users;
        })
        .catch(err => {
          this.errors.push(...this.$helpers.formatErrors(err));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    deleteRelation(id) {
      this.userToRemove = id;
      this.$refs['confirm-remove-modal'].show();
    },
    onDeleteRelationSubmit() {
      this.loading = true;
      axios
        .delete(`${this.$api_url}/organizations/${this.organization.organizationId}/users/${this.userToRemove}/delete`, this.getReqConfig())
        .then(() => {
          this.loadOrganization(this.organization.organizationId);
        })
        .catch(err => {
          this.errors.push(...this.$helpers.formatErrors(err));
        })
        .finally(() => {
          this.userToRemove = null;
          this.loading = false;
        });
    },
    onDeleteRelationCancel() {
      this.userToRemove = null;
    },
    showUserModal() {
      this.$refs['add-org-user-modal'].show();
    },
    onAddOrgUserSubmit() {
      this.loadOrganization(this.organization.organizationId);
    },
    onEditOrganizationSubmit() {
      //We reload the page because the logo image may have changed
      location.reload();
    },
    getRoleClass(role) {
      if (role === "Owner") {
        return "owner-background";
      } else if (role === "Contractor") {
        return "contractor-background";
      } else {
        return "";
      }
    },
    getLevelName(level) {
      if (level === "Sponsor") {
        return "Executive";
      } else {
        return "";
      }
    },
    editOrganization() {
      this.$refs['edit-organization-modal'].show();
    }
  }
}

</script>
<style>
.flag {
  text-align: center;
}
.owner-background {
  background-color: #a4b7c5;
}
.contractor-background {
  background-color: #99df8d;
}</style>